/**
 * The referral letter that travels with her (or her newborn): who she is, why she is referred,
 * what was found at that visit, the latest of what the pack lists for a receiving facility, what is
 * still outstanding, and the referring provider. One printed page.
 */
import {
  type ConceptDef,
  type Observation,
  type Patient,
  type PatientRecord,
  type ServiceRequest,
  conceptOf,
  daysBetween,
  isLabOrder,
  motherOf,
  observationValue,
  splitRecord,
  transportOf,
  urgencyOf,
} from '@assist/core';
import { useEffect, useState } from 'preact/hooks';
import { app, clock } from '../controller.js';
import { eth } from '../dates.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks, allPacks } from '../packs.js';
import { ageOf, displayName, mrnOf } from '../patient.js';
import { PatientChrome } from '../shell.js';
import { isAbnormal, showValue } from './FullRecord.js';

const concepts = new Map<string, ConceptDef>();
for (const p of allPacks)
  for (const c of p.concepts) if (!concepts.has(c.code)) concepts.set(c.code, c);

const usable = (o: Observation) => o.status !== 'entered-in-error' && o.status !== 'cancelled';

function latest(rec: PatientRecord, code: string, before: string, withinDays?: number) {
  return rec.observations
    .filter(
      (o) =>
        usable(o) &&
        conceptOf(o) === code &&
        o.effectiveDateTime <= before &&
        (withinDays === undefined || daysBetween(o.effectiveDateTime, before) <= withinDays),
    )
    .sort((a, b) => b.effectiveDateTime.localeCompare(a.effectiveDateTime))[0];
}

function Field({ code, o }: { code: string; o: Observation }) {
  const c = concepts.get(code);
  const v = observationValue(o);
  return (
    <div class="rec-f">
      <span class="rec-l">{c?.display ?? code}</span>
      <span class={isAbnormal(c, v) ? 'rec-v rec-alarm' : 'rec-v'}>{showValue(c, v)}</span>
    </div>
  );
}

export function ReferralLetter({ id, srId }: { id: string; srId: string }) {
  const a = useApp();
  const [p, setP] = useState<Patient | null>(null);
  const [rec, setRec] = useState<PatientRecord | null>(null);
  const [mother, setMother] = useState<{ p: Patient; rec: PatientRecord } | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh when the store changes
  useEffect(() => {
    void app.session?.get<Patient>('Patient', id).then(async (x) => {
      setP(x);
      const mid = motherOf(x);
      if (mid && app.session) {
        const [mp, mr] = await Promise.all([
          app.session.get<Patient>('Patient', mid),
          app.session.forPatient(mid),
        ]);
        if (mp) setMother({ p: mp, rec: splitRecord(mr) });
      }
    });
    void app.session?.forPatient(id).then((rs) => setRec(splitRecord(rs)));
  }, [id, a.status]);
  if (!p || !rec) return null;
  const sr: ServiceRequest | undefined =
    rec.referrals.find((r) => r.id === srId) ??
    [...rec.referrals]
      .filter((r) => !isLabOrder(r))
      .sort((x, y) => (y.authoredOn ?? '').localeCompare(x.authoredOn ?? ''))[0];
  const at = sr?.authoredOn ?? clock.now();
  const atVisit = sr?.encounter?.reference
    ? rec.observations.filter(
        (o) => usable(o) && o.encounter?.reference === sr.encounter?.reference,
      )
    : [];
  const shownAtVisit = new Set(atVisit.map((o) => conceptOf(o)));
  const sections = activePacks(a.packSettings).flatMap(({ pack }) => pack.letter ?? []);
  const age = ageOf(p, clock.today());
  const pending = rec.referrals.filter((r) => isLabOrder(r) && r.status === 'active');
  const u = a.session?.user;
  return (
    <section>
      <PatientChrome id={id} here="record" />
      <div class="card rec letter" data-card="referral-letter">
        <div class="dhead">
          <div>
            <h3>{t.referralLetter}</h3>
            <p class="muted">
              {u?.facilityName} · {eth(clock.today())}
            </p>
          </div>
          <button type="button" class="sec noprint" onClick={() => window.print()}>
            {t.print}
          </button>
        </div>
        {mother ? (
          <div class="alert">
            <strong>{t.forNewborn}</strong> {t.forNewbornHelp}
          </div>
        ) : null}
        <div class="rec-b">
          <div class="rec-g">
            {(
              [
                [t.nameCol, displayName(p)],
                [t.mrn, mrnOf(p)],
                [t.ageLabel, age ? t.age(age) : undefined],
                [
                  t.sex,
                  p.gender === 'female' ? t.female : p.gender === 'male' ? t.male : undefined,
                ],
                ...(mother
                  ? ([
                      [t.motherCol, displayName(mother.p)],
                      [`${t.motherCol} MRN`, mrnOf(mother.p)],
                    ] as Array<[string, string | undefined]>)
                  : []),
              ] as Array<[string, string | undefined]>
            )
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div class="rec-f" key={k}>
                  <span class="rec-l">{k}</span>
                  <span class="rec-v">{v}</span>
                </div>
              ))}
          </div>
        </div>
        <div class="rec-b">
          <h5>{t.whyReferred}</h5>
          {sr ? (
            <div class="rec-g">
              <div class="rec-f">
                <span class="rec-l">{t.referTo}</span>
                <span class="rec-v">{sr.code?.text}</span>
              </div>
              <div class="rec-f">
                <span class="rec-l">{t.urgencyLabel}</span>
                <span class={urgencyOf(sr) === 'routine' ? 'rec-v' : 'rec-v rec-alarm'}>
                  {t.urgencies[urgencyOf(sr)]}
                  {transportOf(sr) ? ` · ${t.transport}: ${transportOf(sr)}` : ''}
                </span>
              </div>
              <div class="rec-f">
                <span class="rec-l">{t.dateCol}</span>
                <span class="rec-v">{eth(at)}</span>
              </div>
              <div class="rec-f wide">
                <span class="rec-l">{t.reasonLabel}</span>
                <span class="rec-v">{sr.reasonCode?.[0]?.text}</span>
              </div>
            </div>
          ) : (
            <p class="muted">{t.noReferral}</p>
          )}
        </div>
        {atVisit.length ? (
          <div class="rec-b">
            <h5>{t.foundAtVisit}</h5>
            <div class="rec-g">
              {atVisit.map((o) => (
                <Field key={o.id} code={conceptOf(o) ?? ''} o={o} />
              ))}
            </div>
          </div>
        ) : null}
        {sections.map((s) => {
          const r = s.mother ? mother?.rec : rec;
          if (!r) return null;
          const found = s.concepts
            .filter((c) => s.mother || !shownAtVisit.has(c))
            .map((c) => [c, latest(r, c, at, s.within_days)] as const)
            .filter(([, o]) => o);
          if (!found.length) return null;
          return (
            <div class="rec-b" key={s.title}>
              <h5>{s.title}</h5>
              <div class="rec-g">
                {found.map(([c, o]) => (
                  <Field key={c} code={c} o={o as Observation} />
                ))}
              </div>
            </div>
          );
        })}
        {pending.length ? (
          <div class="rec-b">
            <h5>{t.outstanding}</h5>
            <ul class="plain">
              {pending.map((l) => (
                <li key={l.id}>
                  {l.code?.text} · {eth(l.authoredOn ?? '')}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div class="rec-b">
          <h5>{t.referringProvider}</h5>
          <p>{[u?.fullName, u?.facilityName, eth(clock.today())].filter(Boolean).join(' · ')}</p>
          <p>{t.signatureLine}</p>
        </div>
      </div>
    </section>
  );
}
